/**
 * Sent as the planner's next turn by delegateExecuteToExecutor.ts when a background taskType
 * EXECUTE delegation carries no typed instructions. The server brief already supplies the
 * DoBox (the executor conversation's own system prompt), so unlike buildAgentRunPrompt's
 * TECHNICAL_ANALYSIS/IMPLEMENT_AND_PR instructions this carries no `[dobox-context]` token and
 * no <dobox_task> block of its own. Also matched verbatim by the web chat adapter
 * (agentRunToChatItems.ts's isProtocolUserMessage) to hide this machine-written bubble —
 * keep both sides in sync if this text ever changes.
 */
export const EXECUTE_INSTRUCTION =
  'Resolve this DoBox using the tools available in this panel, under the executor policy ' +
  'already in effect for this conversation. Work through what the ticket needs, then leave ' +
  'it in a clear end state: done, blocked with a specific reason, or a question for the owner.';

/**
 * Sent as the planner's turn by agentTrialRun.ts's startAgentTrialRun when the owner tests a
 * draft agent on the DoBox item it was created from. Machine-written like EXECUTE_INSTRUCTION,
 * so the web chat adapter has to hide it by exact match too (agentRunToChatItems.ts's
 * isProtocolUserMessage; the previous wording goes to LEGACY_AGENT_TRIAL_INSTRUCTIONS) — keep
 * both sides in sync if this text changes.
 *
 * It must not read as a rehearsal: worded as "a trial run" the model described what it would do
 * on some other task instead of acting on the open one. Safety lives in the proposal cards (the
 * owner approves every change), not in the wording. Every comment and page it proposes is posted
 * verbatim on approval, so the text must be grounded in what the run read.
 */
export const AGENT_TRIAL_INSTRUCTION =
  'Do the job described in your agent instructions on this DoBox now; this open item is the ' +
  'task. Read whatever you need from it first. Then work out every change to data the job ' +
  'calls for and call all the matching tools together in one step, once per change: the ' +
  'owner reviews and approves each one as a proposal card before anything changes, and the ' +
  'owner only sees the changes proposed in that first step, so a change you leave for later ' +
  'is never shown. Every comment, page or message you propose is posted exactly as written ' +
  'once approved, so write it for the people who will read it: state only facts you read in ' +
  'this run, and when the job needs data you could not read, propose a step that says what ' +
  'the agent will do with it instead of a finished text with made-up content. Changes in the ' +
  'same step are not applied yet, and a message you propose to send is shown to the owner as ' +
  'a preview and never delivered, so a text must never claim that something was sent, ' +
  'saved or created. When the job sends a message, propose that send as one of the changes ' +
  'in this step, so the owner sees exactly what would go out; when the job also asks for a ' +
  'comment that reports the send, leave that comment out of this step, because it would be ' +
  'posted for a message nobody received, and say in your closing sentences that it follows ' +
  'a real send. When the job needs a send and the allowed actions give you no way to make ' +
  'it, propose nothing that implies it happened and say plainly in your closing sentences ' +
  'that the agent cannot send yet. Do not describe yourself, this run or the setup, and do ' +
  'not mention tool or action names in any text meant for people. Propose instead of ' +
  'describing what you would do, use only the allowed actions listed, and do not ask the ' +
  'owner questions. When done, say in one or two sentences what you found and what you ' +
  'proposed.';

/** Fewest loop turns a DoBox agent may be saved with: the run spends its first turns loading its
 *  instructions and discovering tools before it can read the task and act on it. */
export const DOBOX_AGENT_MIN_MAX_TURNS = 8;
